// 路由：登录页 + 文员区 + 管理员区（role=admin 才能进）
import { PreparePage } from "./pages/PreparePage";
import { useCallback, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AdminShell } from "./components/Layout";
import { DialogProvider } from "./components/Dialogs";
import { AlertsProvider } from "./lib/alerts";
import { AuthProvider, isStaff, useAuth } from "./lib/auth";
import { RealtimeProvider } from "./lib/realtime";
import { LoginPage } from "./pages/LoginPage";
import { TasksPage } from "./pages/TasksPage";
import { TaskStatsPage } from "./pages/TaskStatsPage";
import { NewTaskPage } from "./pages/NewTaskPage";
import { TaskDetailPage } from "./pages/TaskDetailPage";
import { NewTypePage } from "./pages/NewTypePage";
import { HealthPage } from "./pages/admin/HealthPage";
import { TypeDetailPage } from "./pages/admin/TypeDetailPage";
import { TypesPage } from "./pages/admin/TypesPage";
import { ComparePage } from "./pages/admin/ComparePage";
import { AgentsMdPage } from "./pages/admin/AgentsMdPage";
import { UsersPage } from "./pages/admin/UsersPage";
import { SystemPage } from "./pages/admin/SystemPage";
import { SponsorMailsPage } from "./pages/SponsorMailsPage";

function RequireAuth({ children, role }: { children: ReactNode; role?: "admin" | "staff" }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="empty">加载中…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  if (role === "admin" && user.role !== "admin") return <div className="empty">只有管理员可以访问这个页面。</div>;
  if (role === "staff" && !isStaff(user.role)) return <div className="empty">没有权限访问这个页面。</div>;
  return <>{children}</>;
}

/** 旧网址跳到新位置，带上原来的筛选参数 */
function KeepQuery({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function Shell() {
  const navigate = useNavigate();
  const onUnauthorized = useCallback(() => navigate("/login", { replace: true }), [navigate]);
  return (
    <AuthProvider onUnauthorized={onUnauthorized}>
      <AuthedProviders>
        <DialogProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* 全屏准备文件：不带侧栏 */}
            <Route path="/tasks/:id/prepare" element={<RequireAuth><PreparePage /></RequireAuth>} />
            <Route
              element={
                <RequireAuth>
                  <AdminShell />
                </RequireAuth>
              }
            >
              <Route index element={<TasksPage />} />
              {/* 旧网址「全部任务」：合进了首页「任务」，带着筛选跳过去 */}
              <Route path="all-tasks" element={<KeepQuery to="/" />} />
              <Route path="stats" element={<TaskStatsPage />} />
              <Route path="tasks/new" element={<NewTaskPage />} />
              <Route path="tasks/:id/edit" element={<NewTaskPage />} />
              <Route path="tasks/:id" element={<TaskDetailPage />} />
              {/* 待我审核 / AI 建议已去掉（2026-10-30）：旧网址跳到任务页 */}
              <Route path="reviews" element={<Navigate to="/?status=review_required" replace />} />
              <Route path="sponsor-mails" element={<RequireAuth role="staff"><SponsorMailsPage /></RequireAuth>} />
              <Route path="proposals" element={<Navigate to="/" replace />} />
              <Route path="types/new" element={<RequireAuth role="admin"><NewTypePage /></RequireAuth>} />
              <Route path="admin" element={<RequireAuth role="admin"><HealthPage /></RequireAuth>} />
              <Route path="admin/types" element={<RequireAuth role="admin"><TypesPage /></RequireAuth>} />
              <Route path="admin/types/:id" element={<RequireAuth role="admin"><TypeDetailPage /></RequireAuth>} />
              <Route path="admin/compare/:group" element={<RequireAuth role="admin"><ComparePage /></RequireAuth>} />
              <Route path="admin/test-tasks" element={<RequireAuth role="admin"><TasksPage test /></RequireAuth>} />
              <Route path="admin/proposals" element={<Navigate to="/" replace />} />
              <Route path="admin/agents-md" element={<RequireAuth role="admin"><AgentsMdPage /></RequireAuth>} />
              <Route path="admin/users" element={<RequireAuth role="admin"><UsersPage /></RequireAuth>} />
              <Route path="admin/system" element={<RequireAuth role="admin"><SystemPage /></RequireAuth>} />
              <Route path="*" element={<div className="empty">页面不存在</div>} />
            </Route>
          </Routes>
        </DialogProvider>
      </AuthedProviders>
    </AuthProvider>
  );
}

/** 实时通道与告警依赖登录 token */
function AuthedProviders({ children }: { children: ReactNode }) {
  const { token, user } = useAuth();
  return (
    <RealtimeProvider token={user ? token : null}>
      <AlertsProvider>{children}</AlertsProvider>
    </RealtimeProvider>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}
